import { yupResolver } from '@hookform/resolvers/yup'
import { Dialog, Typography, IconButton, DialogTitle, DialogContent, DialogActions, DialogContentText, Box, Stack } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { MButton, TextInput } from 'components'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { APIs } from 'services'
import { useAuth } from 'context/auth'
import { USER_TOKEN } from 'config/constants'
import { useLocalStorage } from 'utils/hooks'
import { useSnackbar } from 'notistack'
import { color } from 'config/color'
import { LinkAccountMode } from '../Account'

interface ILinkAccount {
  open: boolean
  mode: LinkAccountMode.LINK | LinkAccountMode.UNLINK
  handleClose: () => void
  accountId: string
}

interface VerifyAccountType {
  accountNumber: string
  serviceAddress: string
}


const verifySchema = yup.object({
  accountNumber: yup.string().required('Account number is required'),
  serviceAddress: yup.string().required('Service address is required')
})

const LinkAccount: React.FC<ILinkAccount> = ({ open, handleClose, mode, accountId: unlinkAccountId }) => {
  const { account, isAdmin, userEmail, getAccount, currentAccount } = useAuth()
  const [step, setStep] = useState<number>(1)
  const { enqueueSnackbar } = useSnackbar()
  const [authToken] = useLocalStorage(USER_TOKEN, '')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [formData, setFormData] = useState<VerifyAccountType | null>(null)

  const {
    handleSubmit: handleVerifySubmit,
    control: verifyControl,
    formState: { errors: verifyErrors },
    reset: resetVerifyForm
  } = useForm<VerifyAccountType>({
    mode: 'all',
    defaultValues: {
      accountNumber: '',
      serviceAddress: ''
    },
    resolver: yupResolver(verifySchema)
  })

  const verifyAccount = async (data: VerifyAccountType) => {
    try {
      setIsLoading(true)
      const response = await APIs.verifyAccount({
        accountId: data.accountNumber,
        serviceAddress: data.serviceAddress.toUpperCase(),
        AccessToken: authToken
      })
      if (response.status !== 1) {
        throw Error(response.message.message)
      } else {
        enqueueSnackbar('Account verified successfully', { variant: 'success' })
        setFormData(data)
        setStep(2)
      }
    } catch (error: any) {
      enqueueSnackbar(`Failed verifying account: ${error.toString()}`, { variant: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (mode === LinkAccountMode.UNLINK) {
      setStep(2)
    } else {
      setStep(1)
    }
  }, [mode, open])

  const handleUpdateAccount = async () => {
    try {
      setIsLoading(true)

      const updateResponse = await updateUserAccount()
      if (updateResponse.status !== 1) {
        throw new Error(updateResponse.message.message)
      }
      enqueueSnackbar('Account linked successfully.', { variant: 'success' })
      void resetFormState()
    } catch (error: any) {
      enqueueSnackbar(`Failed updating account: ${error.toString()}`, { variant: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  const updateUserAccount = async () => {
    return await APIs.updateUser({
      accountId: account?.myAccount.accountId ?? '',
      AccessToken: authToken,
      email: userEmail,
      admin: isAdmin,
      addRemoveAcctUpdates: [
        {
          accountPropertyManager: account?.myAccount.accountId ?? '',
          accountPropertyOwner: {
            [LinkAccountMode.LINK]: formData?.accountNumber,
            [LinkAccountMode.UNLINK]: unlinkAccountId
          }[mode] ?? '',
          addRemoveAcctFlag: mode === LinkAccountMode.UNLINK ? 'R' : 'A'
        }
      ]
    })
  }

  const resetFormState = async () => {
    setStep(0)
    resetVerifyForm()
    setFormData(null)
    handleClose()

    if (currentAccount?.accountId != null) {
      await getAccount({
        AccessToken: authToken,
        accountId: currentAccount?.accountId,
        admin: isAdmin,
        email: userEmail
      })
    }
  }
  return (
    <Dialog fullWidth open={open} closeAfterTransition>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography fontSize={18} fontWeight={500}>
          Link Account
        </Typography>
        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={(theme) => ({

            color: theme.palette.grey[500]
          })}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <DialogContentText sx={{ mb: 2, textAlign: 'justify' }}>
          {step === 1
            ? <>
              We are happy to offer the ability to manage and make payments on accounts that are not in your name.  Link accounts by entering the account number and the street address, as shown on the bill, for the account you want to link.

              <br />
              <Typography mt={1} fontSize={12} color="red" >NOTE:  By linking an account to your profile, you are assuming online management responsibilities of this account.  The account&apos;s primary customer can no longer view or make payments on this account in their Portal profile.  </Typography>
            </>
            : `Confirm if you want to ${mode.toLowerCase()} this account.`}
        </DialogContentText>
        {step === 1
          ? (
            <>
              <TextInput
                fullWidth
                fieldLabel='Account Number'
                fieldName='accountNumber'
                fieldType='text'
                placeholder='Enter your account number'
                control={verifyControl}
                errors={verifyErrors}
              />
              <TextInput
                fullWidth
                fieldLabel='Street Address'
                fieldName='serviceAddress'
                fieldType='text'
                placeholder='Enter your street address'
                control={verifyControl}
                errors={verifyErrors}
              />

            </>
          )
          : mode === LinkAccountMode.LINK && <Stack direction="column" spacing={2} mb={2}>
            <Box >
              <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400} textTransform="uppercase">Account Number</Typography>
              <Typography textTransform="capitalize" fontSize={16}>{formData?.accountNumber}</Typography>
            </Box>
            <Box>
              <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400} textTransform="uppercase">Service Address</Typography>
              <Typography textTransform="capitalize" fontSize={16}>{formData?.serviceAddress}</Typography>
            </Box>
          </Stack>}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', mb: 5 }}>
        {step === 1
          ? <>
            <MButton
              type='button'
              variant='outlined'
              size='large'
              rounded='true'
              sx={{ width: '200px' }}
              texttransform='none'
              onClick={() => { resetVerifyForm() }}
            >
              Clear
            </MButton>
            <MButton
              type='button'
              variant='contained'
              size='large'
              rounded='true'
              sx={{ width: '200px' }}
              texttransform='none'
              onClick={handleVerifySubmit(verifyAccount)}
              loading={isLoading}
              disabled={isLoading}
            >
              Verify
            </MButton>
          </>
          : <>
            <MButton
              type='button'
              variant='outlined'
              size='large'
              rounded='true'
              sx={{ width: '200px' }}
              texttransform='none'
              onClick={() => { setStep(1) }}
            >
              Back
            </MButton>
            <MButton
              type='button'
              variant='contained'
              size='large'
              rounded='true'
              sx={{ width: '200px' }}
              texttransform='none'
              onClick={() => { void handleUpdateAccount() }}
              loading={isLoading}
              disabled={isLoading}
            >
              Update
            </MButton>
          </>}
      </DialogActions>
    </Dialog>
  )
}

export default LinkAccount
